import { UnlayerEditorWrapper } from '@/components/UnlayerEditorWrapper';
import FileUpload from '@/components/FileUpload';
import { AiTemplateActiveDrawerField } from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/AiTemplateActiveStatus';
import { AiTemplateShareDrawerField } from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/AiTemplateShareDrawerField';
import AiTemplateType from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/AiTemplateType';
import { AiTemplateInstructions } from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/AiTemplateInstructions';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useUnlayerTemplatesEditorContext } from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/UnlayerTemplatesEditorContext';
import { TEMPLATE_MODIFY_DENIED_MESSAGE } from '@/features/templates/templatePermissions';
import { FolioDrawer } from '@/components/Drawers/FolioDrawer';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormLabel,
	Grid,
	Stack,
	TextField,
	Tooltip,
} from '@mui/material';
import React, { useLayoutEffect, useRef, useState } from 'react';

const EditorDrawer = () => {
	const {
		labels,
		drawerState,
		templateForm,
		editorMinHeight,
		patchTemplateValues,
		patchTemplateErrors,
		patchDialog,
		isFetchingTemplateDetail,
		isCreatingTemplate,
		isUpdatingTemplate,
		isDeletingTemplate,
		isSharingTemplate = false,
		isFolioAdmin = false,
		canModifySelectedTemplate = true,
		mergeTags,
		requestCloseDrawer,
		handleSaveTemplate,
		handleOpenSaveAsDialog,
		handleOpenLoadDesignDialog,
		handleInsertNameTag,
		setEditorDesign,
	} = useUnlayerTemplatesEditorContext();
	const { getFeatureFlags } = useFeatureFlagContext();

	const { isOpen, isCreateMode } = drawerState;
	const { values, errors, designToLoad, editorInstanceKey } = templateForm;
	const showAiTemplateFields = Boolean(labels.showAiTemplateFields);
	const supportsAttachments = !showAiTemplateFields;

	const isSaving =
		isCreatingTemplate || isUpdatingTemplate || isDeletingTemplate || isSharingTemplate;
	const isMasterTemplateReadOnly = showAiTemplateFields && values.master;
	const isReadOnly = isMasterTemplateReadOnly || (!isCreateMode && !canModifySelectedTemplate);
	const modifyDeniedTooltip =
		isReadOnly && !isMasterTemplateReadOnly ? TEMPLATE_MODIFY_DENIED_MESSAGE : '';

	const rowSizing = showAiTemplateFields && !values.master ? { xs: 4.5 } : { xs: 6 };

	// How AI templates are used only applies to the Markdown email workflow
	const showInstructions =
		showAiTemplateFields && Boolean(getFeatureFlags('markdownEmailWorkflow')?.active);
	const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);

	// The Load Design JSON button sits below the instructions. While they're collapsed, margins
	// lift it beside them, so expanding and collapsing only animates widths, margins and padding
	// and nothing jumps between lines. The collapsed row is as tall as the taller of the two, and
	// both are centred in it. Sizes are measured with the instructions collapsed.
	const instructionsRef = useRef(null);
	const loadButtonRef = useRef(null);
	const [rowSizes, setRowSizes] = useState({ instructions: 0, button: 0, buttonWidth: 0 });
	useLayoutEffect(() => {
		if (!showInstructions || isInstructionsExpanded) {
			return;
		}
		const button = loadButtonRef.current.getBoundingClientRect();
		setRowSizes({
			instructions: instructionsRef.current.getBoundingClientRect().height,
			button: button.height,
			buttonWidth: button.width,
		});
	}, [isOpen, showInstructions]);
	const isRowCollapsed = !isInstructionsExpanded;
	const rowHeight = Math.max(rowSizes.instructions, rowSizes.button);
	const instructionsPadding = isRowCollapsed ? `${(rowHeight - rowSizes.instructions) / 2}px` : 0;
	const loadButtonMargins = {
		mt: isRowCollapsed ? `${-(rowHeight + rowSizes.button) / 2}px` : 0,
		mb: isRowCollapsed ? `${(rowHeight - rowSizes.button) / 2}px` : 0,
	};

	return (
		<FolioDrawer
			open={isOpen}
			onClose={requestCloseDrawer}
			title={isCreateMode ? labels.createDrawerTitle : labels.editDrawerTitle}
			canMaximise
			maximisedSize='full'
		>
			<Grid container spacing={1}>
				<Grid size={rowSizing}>
					<FormControl fullWidth>
						<FormLabel htmlFor='name'>Template name</FormLabel>
						<TextField
							id='name'
							value={values.name}
							onChange={(event) => {
								patchTemplateValues({ name: event.target.value });
								if (errors.name) {
									patchTemplateErrors({ name: '' });
								}
							}}
							error={Boolean(errors.name)}
							helperText={errors.name}
							fullWidth
							disabled={isReadOnly}
						/>
					</FormControl>
				</Grid>
				<Grid size={rowSizing}>
					<Stack spacing={1}>
						<FormControl fullWidth>
							<FormLabel htmlFor='subject'>Subject</FormLabel>
							<TextField
								id='subject'
								value={values.subject}
								onChange={(event) => {
									patchTemplateValues({ subject: event.target.value });
									if (errors.subject) {
										patchTemplateErrors({ subject: '' });
									}
								}}
								error={Boolean(errors.subject)}
								helperText={errors.subject}
								fullWidth
								disabled={isReadOnly}
							/>
						</FormControl>
						{labels.showInsertClientNameTag && (
							<Box>
								<Button
									variant='text'
									size='small'
									sx={{ textTransform: 'none' }}
									onClick={handleInsertNameTag}
									disabled={isReadOnly}
								>
									Insert {'{{client.name}}'} tag
								</Button>
							</Box>
						)}
					</Stack>
				</Grid>
				{showAiTemplateFields && !values.master && (
					<>
						<Grid size={{ xs: 'grow' }}>
							<AiTemplateType />
						</Grid>
						<AiTemplateActiveDrawerField />

						<Grid size={{ xs: 12 }}>
							<AiTemplateShareDrawerField
								labels={labels}
								templateMaster={values.master}
								templateBrokerGroupIds={values.brokerGroupIds}
								onTemplateBrokerGroupIdsChange={(brokerGroupIds) =>
									patchTemplateValues({ brokerGroupIds })
								}
								disabled={isFetchingTemplateDetail || isSaving || isReadOnly}
								showAdmin={isFolioAdmin}
							/>
						</Grid>
					</>
				)}
				{supportsAttachments && (
					<Grid size={{ xs: 12 }}>
						<FormControl fullWidth>
							<FormLabel>Attachments</FormLabel>
							<FileUpload
								files={values.files ?? []}
								setFiles={(files) => patchTemplateValues({ files })}
								multiple
								loading={isFetchingTemplateDetail || isSaving || isReadOnly}
							/>
						</FormControl>
					</Grid>
				)}
				<Grid
					size={{ xs: 12 }}
					sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
				>
					{showInstructions && (
						<Box
							sx={(theme) => ({
								alignSelf: 'flex-start',
								width: isInstructionsExpanded
									? '100%'
									: `calc(100% - ${rowSizes.buttonWidth}px - ${theme.spacing(2)})`,
								py: instructionsPadding,
								transition: theme.transitions.create(['width', 'padding'], {
									duration: theme.transitions.duration.standard,
								}),
							})}
						>
							<Box ref={instructionsRef}>
								<AiTemplateInstructions
									expanded={isInstructionsExpanded}
									onChange={(event, expanded) =>
										setIsInstructionsExpanded(expanded)
									}
								/>
							</Box>
						</Box>
					)}
					<Box
						ref={loadButtonRef}
						sx={(theme) => ({
							py: 1,
							...(showInstructions && {
								...loadButtonMargins,
								transition: theme.transitions.create(['margin'], {
									duration: theme.transitions.duration.standard,
								}),
							}),
						})}
					>
						<Button
							variant='outlined'
							disabled={isFetchingTemplateDetail || isSaving || isReadOnly}
							onClick={handleOpenLoadDesignDialog}
						>
							Load Design JSON
						</Button>
					</Box>
				</Grid>
			</Grid>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<UnlayerEditorWrapper
					key={editorInstanceKey}
					minHeight={editorMinHeight}
					initialDesign={null}
					designToLoad={designToLoad}
					mergeTags={mergeTags}
					onChange={(payload) => {
						if (isReadOnly) {
							return;
						}
						setEditorDesign(payload?.design ?? null);
					}}
				/>
			</Box>

			<FolioDrawerFooter>
				<Stack direction='row' spacing={1}>
					<Tooltip title={modifyDeniedTooltip}>
						<span>
							<Button
								variant='outlined'
								color='error'
								disabled={
									isCreateMode ||
									isFetchingTemplateDetail ||
									isSaving ||
									isReadOnly
								}
								onClick={() => patchDialog('delete', { open: true })}
							>
								Delete
							</Button>
						</span>
					</Tooltip>
					{!isCreateMode && (
						<Button
							variant='outlined'
							disabled={isFetchingTemplateDetail || isSaving}
							onClick={handleOpenSaveAsDialog}
						>
							{labels.saveAsDrawerButton}
						</Button>
					)}
					<Tooltip title={modifyDeniedTooltip}>
						<span>
							<Button
								variant='contained'
								disabled={isFetchingTemplateDetail || isSaving || isReadOnly}
								onClick={handleSaveTemplate}
							>
								{isCreatingTemplate || isUpdatingTemplate ? (
									<CircularProgress size={18} />
								) : isCreateMode ? (
									labels.createButtonLabel
								) : (
									labels.updateButtonLabel
								)}
							</Button>
						</span>
					</Tooltip>
				</Stack>
			</FolioDrawerFooter>
		</FolioDrawer>
	);
};
export default EditorDrawer;
